import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { palette } from "../../themes/theme";
import { useState } from "react";
import { CustomPolygonClass } from "./utilities/CustomPolygon";
import DarkPrimaryButton from "../PrimaryButton/DarkPrimaryButton";
import { CustomDraggable } from "../Draggable";
import { useSelector } from "react-redux";
import ColorPicker from "../ColorPicker";
import { CustomLogoClass } from "./utilities/CustomLogo";
import mapboxgl from "mapbox-gl";
import HaloEffectRange from "../HaloEffectRange";

export const LogoStyleSetting = ({
  mapRef,
  drawRef,
  resetAllOnceChanged,
  setMarkers,
  selectedLogo,
  setSelectedLogo,
  markers,
  currentMode,
  lastClickButtonRef,
  defaultLogoValue,
  setMapToolBarOptionFlags,
  POICategoryList,
  setPOICategoryList,
  createNewPolygon,
  getCurrentPoiSetting,
  markerRef,
  deletedPoiRef,
}) => {
  const logoStyleSettings = useSelector(
    (state) => state.mapSettings.logoSetting
  );
  const poiStyleSettings = useSelector((state) => state.mapSettings.poiSetting);
  const [logoStyleLocally, setLogoStyleLocally] = useState({
    backgroundColor: selectedLogo.imageBackgroundColor,
    isTransparentBackgroundColor: !selectedLogo.isBackgroundColorTransparent,
    // polygonBorderColor: logoStyleSettings.logoLeaderLineColor,
    // polygonBorderWidth: logoStyleSettings.logoLeaderLineBorderWidth,
    polygonBorderColor: selectedLogo.polyLineColor,
    polygonBorderWidth: selectedLogo.polyLineWidth,
    logoHaloEffect: selectedLogo.logoHaloEffect,
    logoHaloEffect_x: selectedLogo.logoHaloEffect_x,
    logoHaloEffect_y: selectedLogo.logoHaloEffect_y,
    logoHaloEffect_b: selectedLogo.logoHaloEffect_b,
    logoHaloEffect_c: selectedLogo.logoHaloEffect_c,
    isHaloEffectTransparent: selectedLogo.isHaloEffectTransparent,
    isLeaderLineEnabled: selectedLogo.isLeaderLineEnabled,
    width: selectedLogo.width,
    height: selectedLogo.height,
    lat: selectedLogo.lat,
    lng: selectedLogo.lng,
  });

  const changeStyle = (key, value) => {
    switch (key) {
      case "backgroundColor-transparent":
        value = value.target.checked;
        key = "isTransparentBackgroundColor";
        break;
      case "polygonBorderColor-transparent":
        value = value.target.checked;
        key = "isLeaderLineEnabled";
        break;
      case "logoHaloEffect-transparent":
        value = value.target.checked;
        key = "isHaloEffectTransparent";
        break;
      default:
        break;
    }

    const tempStyle = {
      ...logoStyleLocally,
      [key]: value,
    };

    CustomLogoClass.ChangeLogoDesign({
      mapDraw: drawRef.current,
      map: mapRef,
      id: selectedLogo.id,
      connectedLeaderLineId: selectedLogo.connectedLeaderLineId,
      backgroundColor: tempStyle.isTransparentBackgroundColor
        ? tempStyle.backgroundColor
        : "transparent",
      logoHaloEffect: tempStyle.logoHaloEffect,
      logoHaloEffect_x: tempStyle.logoHaloEffect_x,
      logoHaloEffect_y: tempStyle.logoHaloEffect_y,
      logoHaloEffect_b: tempStyle.logoHaloEffect_b,
      logoHaloEffect_c: tempStyle.logoHaloEffect_c,
      isHaloEffectTransparent: tempStyle.isHaloEffectTransparent,
      lineColor: tempStyle.polygonBorderColor,
      width: tempStyle.polygonBorderWidth,
      isLeaderLineEnabled: tempStyle.isLeaderLineEnabled,
      logoDefaultStyle: logoStyleSettings,
      updateAutoSave: () => {},
      setSelectedLogo,
      currentMode,
      polygonDefaultStyle: CustomPolygonClass.polygonDefaultStyle,
      divWidth: tempStyle.width,
      height: tempStyle.height,
      lat: tempStyle.lat,
      lng: tempStyle.lng,
      setUndoHistory: () => {},
      setRedoHistory: () => {},
      setDrawnFeaturesSupport: () => {},
      checkUpdate: true,
      markers,
    });
    setLogoStyleLocally(tempStyle);
  };

  const handleRemove = () => {
    CustomLogoClass.DeleteMarker({
      imageId: selectedLogo.id,
      POICategoryList,
      map: mapRef,
      mapDraw: drawRef.current,
      mapboxgl,
      setPOICategoryList,
      lastClickButton: lastClickButtonRef,
      logoDefaultStyle: logoStyleSettings,
      defaultLogoValue,
      poiSetting: poiStyleSettings,
      setSelectedLogo,
      setMapToolBarOptionFlags,
      setResetMap: () => {},
      createNewPolygon,
      updateAutoSave: () => {},
      resetAllOnceChanged,
      currentMode,
      polygonDefaultStyle: CustomPolygonClass.polygonDefaultStyle,
      getCurrentPoiSetting,
      setDrawnFeaturesSupport: () => {},
      setUndoHistory: () => {},
      setRedoHistory: () => {},
      markerRef,
    });
    setMarkers(markerRef.current);
    setSelectedLogo(null);
    if (selectedLogo.id.startsWith("poiImage^")) {
      const arr = selectedLogo.id.split("^");
      deletedPoiRef.current.push(arr[arr.length - 1]);
    }
  };

  const handleOk = () => {
    // mapRef.getCanvas().style.cursor = "grab";
    // setTextSelected(null);
    resetAllOnceChanged();
    setSelectedLogo(null);
  };

  return (
    <CustomDraggable
      width={300}
      height={360}
      headerTitle={"Logo Settings"}
      customFooter={
        <>
          <Button
            variant="outlined"
            sx={{ fontSize: 12, fontWeight: "bold" }}
            onClick={handleOk}
          >
            Ok
          </Button>
          <Button
            variant="outlined"
            sx={{ fontSize: 12, fontWeight: "bold" }}
            onClick={handleRemove}
          >
            Remove
          </Button>
        </>
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography
          sx={{
            color: palette.text.dark,
            fontSize: 15,
          }}
        >
          Background Settings
        </Typography>
        <ColorPicker
          title={"Fill"}
          //   checked={polygonStyleRef.current.polygonColorTransparent}
          color={logoStyleLocally.backgroundColor}
          checked={logoStyleLocally.isTransparentBackgroundColor}
          setColor={changeStyle}
          type={"backgroundColor"}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{ p: 0 }}
                  defaultChecked={logoStyleLocally.isHaloEffectTransparent}
                  onChange={(e) => changeStyle(`logoHaloEffect-transparent`, e)}
                />
              }
              sx={{ m: 0 }}
            />
            <Typography
              sx={{
                color: palette.text.dark,
                fontSize: 15,
              }}
            >
              Halo Effect
            </Typography>
          </Box>
          <ColorPicker
            width={20}
            height={20}
            color={logoStyleLocally.logoHaloEffect}
            // checked={logoStyleLocally.isHaloEffectTransparent}
            rightAlign={true}
            noCheckbox={true}
            setColor={changeStyle}
            type={"logoHaloEffect"}
          />
        </Box>

        <HaloEffectRange
          title="B"
          value={logoStyleLocally.logoHaloEffect_b}
          type="logoHaloEffect_b"
          onChange={changeStyle}
          min={0}
          max={10}
          disabled={!logoStyleLocally.isHaloEffectTransparent}
        />

        <HaloEffectRange
          title="X"
          type="logoHaloEffect_x"
          value={logoStyleLocally.logoHaloEffect_x}
          onChange={changeStyle}
          min={-20}
          max={20}
          disabled={!logoStyleLocally.isHaloEffectTransparent}
        />

        <HaloEffectRange
          title="Y"
          type="logoHaloEffect_y"
          value={logoStyleLocally.logoHaloEffect_y}
          onChange={changeStyle}
          min={-20}
          max={20}
          disabled={!logoStyleLocally.isHaloEffectTransparent}
        />

        <Typography
          sx={{
            color: palette.text.dark,
            fontSize: 15,
          }}
        >
          Leader Line Settings
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <ColorPicker
            // title={"Border"}
            // checked={polygonStyleRef.current.polygonBorderColorTransparent}
            // color={polygonStyleRef.current.polygonBorderColor}
            checked={logoStyleLocally.isLeaderLineEnabled}
            color={logoStyleLocally.polygonBorderColor}
            setColor={changeStyle}
            type={"polygonBorderColor"}
          />
          <TextField
            type="number"
            sx={{
              width: "65px",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "white",
                color: "black",
                borderRadius: "4px",
                "& fieldset": {
                  borderColor: "black", // default border
                },
                "&:hover fieldset": {
                  borderColor: "black", // on hover
                },
                "&.Mui-focused fieldset": {
                  borderColor: "black", // on focus
                },
              },
              "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                {
                  display: "none",
                },
              "& input[type=number]": {
                MozAppearance: "textfield",
              },
              "& .MuiInputLabel-root": {
                color: "black", // label color
              },
            }}
            label="Width"
            defaultValue={logoStyleLocally.polygonBorderWidth}
            onChange={(e) => changeStyle("polygonBorderWidth", e.target.value)}
          />
        </Box>
      </Box>
    </CustomDraggable>
  );
};
